<template>
  <div class="cart-drawer critical-component-hide">
    <div class="cart-drawer__title-section mb-m">
      <div class="cart-drawer__title-wrapper">
        <icon-cart-empty
          v-if="cart.item_count === 0"
        />

        <icon-cart
          v-else
        />

        <h2
          id="overlay-cartDrawer"
          class="text-heading-xl"
          v-html="cartTitle"
        />
      </div>

      <button
        class="icon-button"
        type="button"
        @click.prevent="close"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.close')"
        />

        <icon-close />
      </button>
    </div>

    <free-shipping-indicator
      class="cart-drawer__free-shipping-indicator mt-m"
      :free-shipping-threshold="freeShippingThreshold"
      :free-shipping-enabled="freeShippingEnabled"
      :excluded="{
        property: 'tags',
        value: ['freeshipping_excluded'],
        condition: 'all'
      }"
    />

    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem, index) of cartItems"
          :key="getCartItemKey(lineItem)"
        >
          <line-item
            class="cart-drawer__line-item"
            :line-item="lineItem"
            small
          />

          <divider-line v-if="(index + 1) !== cartItems.length" />
        </template>

        <recommendations-carousel
          v-if="isCarouselPopulated"
          :bloomreach-recommendation-id="bloomreachRecommendationId"
          class="cart-drawer__recommendations-carousel"
          :collection="collection"
          :config="recommendationsCarouselConfig"
          :heading="heading"
          :initial-recommendations-state="initialRecommendationsState"
          :limit="limit"
          :liquid="liquid"
          :product-data-source="productDataSource"
          :reference-product-id="cartItems[0].id"
          :use-cache="false"
        />
      </div>

      <cart-summary
        v-if="cart.item_count > 0"
        block-class="cart-drawer"
        class="cart-drawer__footer"
        small
      />
    </form>

    <div
      v-else
      class="cart-drawer__empty-state"
      js-overlay="scrollElement"
    >
      <div class="cart-drawer__cta-container">
        <p
          class="
              cart-drawer__empty
              text-body-s-regular
              mb-m
            "
          v-text="$string('cart.empty.drawer_text')"
        />

        <div
          v-if="empty.ctaText"
          class="cart-drawer__empty-cta-container"
        >
          <btn
            class="cart-drawer__empty-cta"
            :label="empty.ctaText"
            :url="empty.ctaUrl"
          />
        </div>
      </div>

      <recommendations-carousel
        v-if="isCarouselPopulated"
        :bloomreach-recommendation-id="bloomreachRecommendationId"
        class="cart-drawer__recommendations-carousel"
        :collection="collection"
        :config="recommendationsCarouselConfig"
        :heading="heading"
        :initial-recommendations-state="initialRecommendationsState"
        :limit="limit"
        :liquid="liquid"
        :product-data-source="productDataSource"
        :use-cache="false"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} empty - Empty cart CTA settings.
 * @param {Number} freeShippingThreshold - Threshold value.
 * @param {Boolean} freeShippingEnabled - Enabled.
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapState } from 'vuex'

import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import FreeShippingIndicator
  from '~async/free-shipping-indicator/free-shipping-indicator.vue'
import LineItem from '~async/line-item/line-item'
import Btn from '~global/btn/btn.vue'
import RecommendationsCarousel from '~async/recommendations-carousel/recommendations-carousel'

import IconCart from '~icons/general/cart.svg'
import IconCartEmpty from '~icons/general/cart-empty.svg'
import IconClose from '~icons/directional-navigation/close.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'CartDrawer',

  components: {
    IconCart,
    IconCartEmpty,
    IconClose,
    Btn,
    CartSummary,
    DividerLine,
    FreeShippingIndicator,
    LineItem,
    RecommendationsCarousel,
  },

  props: {
    bloomreachRecommendationId: {
      type: String,
      default: '',
    },
    collection: {
      type: [Boolean, Object],
      default: false,
    },
    empty: {
      type: Object,
      default: () => ({
        ctaText: '',
        ctaUrl: '',
      }),
    },
    freeShippingThreshold: {
      type: Number,
      default: 0,
    },
    freeShippingEnabled: {
      type: Boolean,
      default: true,
    },
    heading: {
      type: Object,
      default: () => ({
        classes: {},
        heading: '',
      }),
    },
    initialRecommendationsState: {
      type: String,
      default: 'loading',
    },
    limit: {
      type: Number,
      default: 8,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    productDataSource: {
      type: String,
      default: 'shopify',
      validator: (value) => ['bloomreach', 'shopify'].includes(value),
    },
  },

  data() {
    return {
      recommendationsCarouselConfig: {
        arrows: false,
        breakpoints: {
          [breakpoints.l]: {
            fixedWidth: '83%',
            padding: {
              left: 'var(--layout-mobile-gutter)',
              right: 'var(--layout-mobile-gutter)',
            },
            pagination: true,
            perMove: 1,
            perPage: 1,
          },
        },
        direction: 'ltr',
        drag: true,
        fixedWidth: '50%',
        gap: 'var(--layout-mobile-gutter)',
        padding: {
          left: 'var(--layout-desktop-gutter)',
          right: 'var(--layout-desktop-gutter)',
        },
        pagination: true,
        pauseOnHover: false,
        perMove: 1,
        perPage: 2,
        speed: this.$timing('normal'),
        start: 0,
        type: 'slide',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

    /**
     * Cart title with cart item count
     * @return {string}
     */
    cartTitle() {
      return this.$string(
        'cart.title',
        { replace: { count: this.cart.item_count } },
      )
    },

    /**
     * Compute if recommendations carousel has valid data.
     * @returns {Boolean}
     */
    isCarouselPopulated() {
      if (this.productDataSource === 'shopify') {
        return this.collection
      }

      return this.bloomreachRecommendationId
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap and send tracking events.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.update({ currentElement: document.activeElement })
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    this.overlay.update()
  },

  methods: {

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Close cart drawer.
     */
    close() {
      this.overlay.close()
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
